import React, { useMemo } from "react";

import { DeleteTwoTone, PlusOutlined } from "@ant-design/icons";
import { Radio, Space, InputNumber, Select, Form, Button } from "antd";
import { FormListFieldData } from "antd/lib/form/FormList";
import { RadioChangeEvent } from "antd/lib/radio";

import { Container } from "./styles";

import { useEditServiceContext } from "components/ratecard/contextProviders";

import useImportedCarriers from "../../../../pages/_common/ShippingCostsPage/Api/useImportCarriers";

type UnitsOption = "percentage" | "fixed";

const optionUnitsSymbolMap: Record<UnitsOption, string> = {
  percentage: "%",
  fixed: "$",
};

export interface PostageFeeValue {
  number?: number;
  units?: UnitsOption;
  carrier?: string;
}

interface PostageFeeInputProps {
  value?: PostageFeeValue;
  onChange?: (value: PostageFeeValue) => void;
}

const PostageFeeInput: React.FC<PostageFeeInputProps> = ({ value = {}, onChange }) => {
  const [number, setNumber] = React.useState(0);
  const [units, setUnits] = React.useState<UnitsOption>("percentage");
  const { allowEditing } = useEditServiceContext();
  const { data: importedCarriers } = useImportedCarriers();
  const [carrierID, setCarrierID] = React.useState("");

  const triggerChange = (changedValue: any) => {
    if (onChange) {
      onChange({ number, units, carrierID, ...value, ...changedValue });
    }
  };

  const onNumberChange = (newValue: string | number | undefined) => {
    const newNumber = typeof newValue === "string" ? parseInt(newValue, 10) : newValue;
    if (Number.isNaN(number)) {
      return;
    }
    if (!("number" in value) && newNumber) {
      setNumber(newNumber);
    }
    triggerChange({ number: newNumber });
  };

  const onUnitsChange = (e: RadioChangeEvent) => {
    if (!("units" in value)) {
      setUnits(e.target.value);
    }
    triggerChange({ units: e.target.value });
  };

  function onCarrierChange(carrier: any) {
    setCarrierID(carrier);
    triggerChange({ carrierID: carrier });
  }

  const addNewPostageFeeField = useMemo(() => {
    return (fields: FormListFieldData[], add: () => void) => {
      // Add new postage fee field when edit button is clicked and there are no form fields in the form
      if (!fields.length && allowEditing) add();
    };
  }, [allowEditing]);

  return (
    <Container>
      <Form.List name="postageFeeList">
        {(fields, { add, remove }) => {
          addNewPostageFeeField(fields, add);
          return (
            <div className="postage-fee-list">
              {fields.map((field, index) => (
                <div key={field.key}>
                  <Space direction="horizontal" size={16} align={"start"}>
                    <Form.Item
                      label={allowEditing ? "Carrier" : null}
                      name={[index, "carrier"]}
                      rules={[{ required: true, message: "Carrier is required" }]}
                      initialValue={"-1"}
                    >
                      <Select
                        onChange={(value) => onCarrierChange(value)}
                        size={"middle"}
                        style={{ width: 150 }}
                        disabled={!allowEditing}
                      >
                        <Select.Option value={"-1"} key={"Default"}>
                          {"Default"}
                        </Select.Option>
                        {importedCarriers!.map((carrier) => (
                          <Select.Option value={carrier.id} key={carrier.name}>
                            {carrier.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      label={allowEditing ? "Value" : null}
                      name={[index, "value"]}
                      rules={[{ required: true, message: "Value is required" }]}
                    >
                      <InputNumber
                        value={number}
                        onChange={onNumberChange}
                        style={{ width: 100 }}
                        precision={2}
                        step={0.1}
                        disabled={!allowEditing}
                        min={0}
                      />
                    </Form.Item>
                    <Form.Item
                      label={allowEditing ? "Unit" : null}
                      name={[index, "unit"]}
                      rules={[{ required: true, message: "Unit is required" }]}
                      initialValue={units}
                    >
                      <Radio.Group
                        value={value.units || units}
                        buttonStyle="solid"
                        onChange={onUnitsChange}
                        defaultValue={units}
                        disabled={!allowEditing}
                      >
                        <Radio.Button value="percentage">{optionUnitsSymbolMap["percentage"]}</Radio.Button>
                        <Radio.Button value="fixed">{optionUnitsSymbolMap["fixed"]}</Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                    {fields.length >= 2 && field.key !== 0 && allowEditing ? (
                      <Button onClick={() => remove(field.name)} icon={<DeleteTwoTone twoToneColor="#eb2f96" />} />
                    ) : null}
                  </Space>
                </div>
              ))}
              {allowEditing && (
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  size="middle"
                  style={{ paddingLeft: 0 }}
                  onClick={() => {
                    add();
                  }}
                >
                  Add Postage Fee
                </Button>
              )}
            </div>
          );
        }}
      </Form.List>
    </Container>
  );
};

export default PostageFeeInput;
